import type { BoardroomDeliverable } from '../../../types';
import { matchLine } from '../reports/tableFilter';
import { subjectTag } from './provenance';

/**
 * A table as an entry on the room's list: what it is, one number, and whether
 * anything landed in it while you were looking elsewhere - the row count and
 * the fact that something arrived, not a progress bar over a total nobody can
 * know.
 */
export function fillLine(d: BoardroomDeliverable): string {
  const { rows, holes } = d.fill;
  const what = d.fill.words ?? d.fill.what;
  if (!rows && !holes) return 'nothing yet';
  const parts = [`${rows} ${rows === 1 ? 'row' : 'rows'}`];
  if (holes) parts.push(`${holes} gaps: ${what.slice(0, 2).join(', ') || 'something'}`);
  return parts.join(' · ');
}

export default function DeliverableTile({
  deliverable,
  badge,
  onOpen,
  onPin,
  selected = false,
  glow = false,
  fill = false,
  matches = null,
  title,
}: {
  /** The name to show instead of the table's own: its view's, on the list. */
  title?: string;
  /** How it fares against the room's filter; null when there is none. */
  matches?: { hits: number; seen: number } | null;
  deliverable: BoardroomDeliverable;
  /** The table open beside the tiles: marked, so you can see where you are. */
  selected?: boolean;
  /** Just added to the list: it glows for a few seconds so you can see where. */
  glow?: boolean;
  /** How many items landed in it since this reader last looked. */
  badge?: number;
  onOpen: () => void;
  /** Take it off the list. Without it the tile has no ×. */
  onPin?: () => void;
  /** Take the width it is given, with no floor: the column beside an open
   *  table is narrower than a tile's usual minimum once its scrollbar shows. */
  fill?: boolean;
}) {
  const d = deliverable;
  return (
    <div
      className={`group relative flex flex-col justify-between rounded-lg border px-3 py-2 ${glow ? 'jc-just-added ' : ''}${fill ? 'w-full min-w-0' : 'min-w-[11rem]'} ${
        // NO MATCH FADES, it does not vanish: the board keeps its shape.
        matches && !matches.hits ? 'opacity-40 ' : ''
      }${
        selected
          ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400 dark:border-indigo-500 dark:bg-indigo-900/30 dark:ring-indigo-500'
          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
      }`}
      data-testid={`deliverable-tile-${d.kind}`}
      data-just-added={glow || undefined}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected ? 'page' : undefined}
        className={`text-left min-w-0 ${onPin ? 'pr-6' : ''}`}
        title={d.answers}
      >
        {/* THE NAME WRAPS, to two lines: "Brand searches · …" cut off the
            one part that told three of them apart. */}
        <div className="flex items-start gap-1.5 min-w-0">
          <span
            className={`line-clamp-2 break-words text-xs font-medium ${
              selected ? 'text-indigo-800 dark:text-indigo-200' : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {title ?? d.title}
          </span>
          {subjectTag(d) && (
            <span
              className="shrink-0 rounded bg-gray-100 px-1 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              data-testid={`subject-tag-${d.id}`}
            >
              {subjectTag(d)}
            </span>
          )}
          {d.lens?.tags?.length ? (
            <span className="shrink-0 text-[11px] text-indigo-600 dark:text-indigo-300">
              ×{d.lens.tags.length}
            </span>
          ) : null}
          {!!badge && (
            <span className="shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-900/40 px-1.5 text-[11px] text-indigo-700 dark:text-indigo-200">
              +{badge}
            </span>
          )}
        </div>
        {matches ? (
          <div
            className={`truncate text-xs ${
              matches.hits ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'
            }`}
            data-testid={`tile-matches-${d.kind}`}
          >
            {matchLine(matches)}
          </div>
        ) : (
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">{fillLine(d)}</div>
        )}
      </button>
      {onPin && (
        <div className="absolute right-1 top-1 flex items-center text-xs text-gray-400">
          <button
            type="button"
            onClick={onPin}
            title="Unpin it from the Dashboard. It waits in All views."
            aria-label={`Unpin ${title ?? d.title} from the Dashboard`}
            className="rounded px-1 hover:text-indigo-600 dark:hover:text-indigo-300"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
